import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { format, eachDayOfInterval, eachHourOfInterval, differenceInHours } from "date-fns";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  ArrowDownToLine, ArrowUpFromLine, Building2, TrendingUp, CheckCircle2,
  XCircle, Activity, Wallet, RefreshCw, Users, Server, Zap, Sparkles,
  ArrowUpRight, ChevronRight,
} from "lucide-react";
import EngineLayout from "@/shared/components/engine/EngineLayout";
import DateFilter from "@/shared/components/dashboard/DateFilter";
import { getDateRange, filterTransactionsByDateRange } from "@/shared/utils/dateFilters";
import * as adminService from "@/shared/api/services/adminService";
import * as engineService from "@/shared/api/services/engineService";
import * as combinedTransactionService from "@/shared/api/services/combinedTransactionService";
import { auth } from "@/shared/api/graphqlClient";
import AdminTransactionTable from "@/shared/components/admin/AdminTransactionTable";
import TransactionDetailPanel from "@/shared/components/admin/TransactionDetailPanel";

const SUCCESS_STATUSES = new Set(["success", "completed", "succeeded", "approved", "processed"]);

function normalizeTxType(type) {
  const normalized = String(type || "").toLowerCase();
  if (["withdrawal", "withdraw", "cashout", "payout"].includes(normalized)) return "withdrawal";
  if (["deposit", "sale", "payin", "topup", "qr"].includes(normalized)) return "deposit";
  return "unknown";
}

function parseAmount(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const parsed = Number.parseFloat(String(value ?? "0"));
  return Number.isFinite(parsed) ? parsed : 0;
}


/* ─── Premium Stat Card ──────────────────────────────────────── */
function PremiumStatCard({ title, value, trend, trendPositive, icon: Icon, gradient, accent, delay }) {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 28, scale: 0.93 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay, duration: 0.5, type: "spring", stiffness: 180, damping: 18 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      className="relative"
      style={{ perspective: 1000 }}
    >
      {/* Radial glow on hover */}
      <motion.div
        animate={{ opacity: hovered ? 1 : 0, scale: hovered ? 1.08 : 0.85 }}
        transition={{ duration: 0.35 }}
        className="absolute inset-0 rounded-2xl pointer-events-none"
        style={{ background: `radial-gradient(circle at 50% 50%, ${accent}1e, transparent 68%)`, filter: "blur(10px)" }}
      />

      {/* Card */}
      <motion.div
        animate={{
          y: hovered ? -8 : 0,
          rotateX: hovered ? -2 : 0,
          rotateY: hovered ? 1.5 : 0,
        }}
        transition={{ type: "spring", stiffness: 380, damping: 24, mass: 0.85 }}
        className="relative rounded-2xl overflow-hidden h-full"
        style={{
          background: "linear-gradient(135deg, rgba(15,23,42,0.85) 0%, rgba(8,14,30,0.9) 100%)",
          border: `1px solid ${hovered ? `${accent}38` : "rgba(148,163,184,0.1)"}`,
          boxShadow: hovered
            ? `0 22px 60px rgba(0,0,0,0.55), 0 0 32px ${accent}18`
            : "0 4px 20px rgba(0,0,0,0.35)",
          transition: "border-color 0.3s, box-shadow 0.3s",
          transformStyle: "preserve-3d",
          padding: 20,
        }}
      >
        {/* Top shimmer */}
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${accent}55, transparent)` }} />

        {/* Animated bottom accent line */}
        <motion.div
          animate={{ x: hovered ? "105%" : "-105%" }}
          transition={{ duration: 1.1, ease: "easeInOut" }}
          className="absolute bottom-0 left-0 right-0 h-[2px]"
          style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
        />

        {/* BG mesh */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div style={{ position: "absolute", top: -24, right: -24, width: 110, height: 110, borderRadius: "50%", background: `${accent}0a`, filter: "blur(18px)" }} />
        </div>

        <div className="relative flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">{title}</p>
            <motion.p
              animate={{ scale: hovered ? 1.04 : 1 }}
              transition={{ duration: 0.25 }}
              className="text-2xl font-bold text-slate-100 leading-none mb-1.5"
            >
              {value}
            </motion.p>
            {trend && (
              <div className="flex items-center gap-1">
                <ArrowUpRight
                  className="h-3 w-3"
                  style={{
                    color: trendPositive ? "#34d399" : "#f87171",
                    transform: trendPositive ? "none" : "rotate(90deg)",
                  }}
                />
                <span className="text-xs font-bold" style={{ color: trendPositive ? "#34d399" : "#f87171" }}>
                  {trend}
                </span>
              </div>
            )}
          </div>

          {/* Icon */}
          <motion.div
            animate={{
              rotateY: hovered ? 16 : 0,
              rotateX: hovered ? -9 : 0,
              scale: hovered ? 1.12 : 1,
            }}
            transition={{ type: "spring", stiffness: 400, damping: 18 }}
            className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, ${gradient})`,
              boxShadow: hovered ? `0 0 22px ${accent}44` : `0 4px 14px ${accent}25`,
              transformStyle: "preserve-3d",
              transition: "box-shadow 0.3s",
            }}
          >
            <Icon className="h-5 w-5 text-white" />
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ─── Custom Chart Tooltip (Matching Admin Style) ───────────────── */
function ChartTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    return (
      <div style={{ backgroundColor: "#0f172a", border: "1px solid #1e3a5f", borderRadius: "10px", padding: "10px 14px", boxShadow: "0 8px 32px rgba(0,0,0,0.55)" }}>
        <p style={{ color: "#94a3b8", fontSize: "12px", marginBottom: "4px" }}>{label}</p>
        {payload.map((entry, i) => (
          <p key={i} style={{ color: entry.color, fontSize: "13px", fontWeight: 600, margin: "2px 0" }}>
            {entry.name}: ฿{Number(entry.value || 0).toLocaleString()}
          </p>
        ))}
      </div>
    );
  }
  return null;
}

/* ─── Quick Action ───────────────────────────────────────────── */
function QuickAction({ to, icon: Icon, label, gradient, accent, delay }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Link to={createPageUrl(to)}>
      <motion.div
        initial={{ opacity: 0, x: 16 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay, type: "spring", stiffness: 280, damping: 24 }}
        onHoverStart={() => setHovered(true)}
        onHoverEnd={() => setHovered(false)}
        whileTap={{ scale: 0.98 }}
        className="group relative overflow-hidden rounded-xl p-3.5 flex items-center gap-4 cursor-pointer"
        style={{
          background: hovered
            ? "rgba(34,211,238,0.06)"
            : "rgba(15,23,42,0.4)",
          border: `1px solid ${hovered ? "rgba(34,211,238,0.22)" : "rgba(148,163,184,0.08)"}`,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{
            background: `linear-gradient(135deg, ${gradient})`,
            boxShadow: hovered ? `0 0 16px ${accent}44` : `0 4px 12px rgba(0,0,0,0.2)`,
            transition: "all 0.3s",
          }}
        >
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold truncate" style={{ color: hovered ? "#22d3ee" : "#cbd5e1" }}>{label}</p>
          <p className="text-[10px] text-slate-500 font-medium truncate">View and manage {label.toLowerCase()}</p>
        </div>
        <ChevronRight className="h-3.5 w-3.5 text-slate-600 transition-all duration-300 group-hover:translate-x-1 group-hover:text-cyan-400" />
      </motion.div>
    </Link>
  );
}

/* ─── Main Page ──────────────────────────────────────────────── */
export default function EngineDashboard() {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const params = useParams();
  const engineId = params.engineId || auth.getUserData()?.userId;
  const [dateFilter, setDateFilter] = useState("today");
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);

  const { data: engine } = useQuery({
    queryKey: ["engine-detail", engineId],
    queryFn: async () => {
      const role = auth.getUserRole();
      if (role === "ADMIN" || role === "STAFF_ADMIN") {
        return adminService.getEngineById(engineId);
      }
      return engineService.getProfile();
    },
    enabled: !!engineId,
  });

  const { data: dashboardMetrics = {
    totalVolume: 0,
    todayVolume: 0,
    totalTransactions: 0,
    successRate: 0,
    resellers: 0,
    merchants: 0,
  }, refetch: refetchDashboardMetrics } = useQuery({
    queryKey: ["engine-dashboard-metrics", engineId],
    queryFn: async () => adminService.getEngineDashboardLiveMetrics(engineId),
    enabled: !!engineId,
  });

  const resellerName = engine?.name || t("engine");


  const { data: allResellers = [], refetch: refetchResellers } = useQuery({
    queryKey: ["all-resellers", engineId],
    queryFn: async () => {
      const role = auth.getUserRole();
      if (role === "ADMIN" || role === "STAFF_ADMIN") {
        return adminService.getAllResellers();
      }
      const res = await engineService.getMyResellers(1, 1000);
      return res.data || [];
    },
    enabled: !!engineId,
  });

  const { data: allMerchants = [], refetch: refetchMerchants } = useQuery({
    queryKey: ["all-merchants", engineId],
    queryFn: async () => {
      const role = auth.getUserRole();
      if (role === "ADMIN" || role === "STAFF_ADMIN") {
        return adminService.getAllMerchants();
      }
      const res = await engineService.getMyMerchants(1, 1000);
      return res.data || [];
    },
    enabled: !!engineId,
  });

  const resellers = React.useMemo(() => {
    if (!engineId) return [];
    const role = auth.getUserRole();
    if (role === "ENGINE" || role === "STAFF_ENGINE") return allResellers;
    return allResellers.filter(r => r.createdByEngineId === engineId);
  }, [allResellers, engineId]);

  const merchants = React.useMemo(() => {
    if (!engineId) return [];
    const role = auth.getUserRole();
    if (role === "ENGINE" || role === "STAFF_ENGINE") return allMerchants;
    const resellerIds = new Set(resellers.map(r => r.id));
    return allMerchants.filter(m => m.createdByEngineId === engineId || resellerIds.has(m.createdByResellerId));
  }, [allMerchants, resellers, engineId]);

  const merchantIdsForQuery = React.useMemo(() => {
    return merchants.map(m => m.id);
  }, [merchants]);

  // 2b. Add a separate query for Recent Transactions (last 5, ignoring date filters)
  const { data: recentTransactionsRaw = [], isLoading: recentLoading, refetch: refetchRecentTransactions } = useQuery({
    queryKey: ["recent-transactions", merchantIdsForQuery],
    queryFn: async () => {
      const merchantsFilter = merchantIdsForQuery.length > 0 ? merchantIdsForQuery : ["__NO_MERCHANTS__"];
      const result = await combinedTransactionService.getCombinedQRCodeAndWitData(
        1,
        5, // User requested last five
        "",
        {}, // No date filters for the recent table
        merchantsFilter,
      );
      return result?.data || [];
    },
    enabled: !!engineId && (allMerchants.length > 0 || allResellers.length > 0)
  });

  const recentTx = React.useMemo(() => {
    return recentTransactionsRaw.map((t) => ({
      ...t,
      transaction_id: t.transaction_id || t.payment_id || t.id,
      merchant_ref: t.merchant_ref || t.reference_id,
      type: normalizeTxType(t.type),
      status: (t.status || "").toLowerCase(),
      amount: parseAmount(t.amount),
      provider_display: t.provider_code || "-",
      currency: t.currency,
      client_bank_name: t.client_bank_name || t.bank_name || t.bank_code,
      bank_name: t.bank_name,
      bank_code: t.bank_code,
      merchant: t.merchant || {
        name: t.merchant_name,
        id: t.merchant_id,
        code: t.merchant_code,
      },
      reseller: t.reseller || {
        name: t.reseller_name,
        id: t.reseller_id,
        code: t.reseller_code,
      },
      created_date: t.created_date || t.createdAt,
      processed_at: t.processed_at || t.updatedAt,
      completed_at: t.processed_at || t.updatedAt,
      is_qr_code: normalizeTxType(t.type) === 'deposit',
    })).sort((a, b) => new Date(b.created_date || b.createdAt).getTime() - new Date(a.created_date || a.createdAt).getTime());
  }, [recentTransactionsRaw]);

  // Fetch chart transactions from DB with date filters applied.
  const chartFilters = React.useMemo(() => {
    const range = getDateRange(dateFilter, customDateRange);
    return {
      startDate: range.startDate.toISOString(),
      endDate: range.endDate.toISOString(),
    };
  }, [dateFilter, customDateRange]);

  const { data: combinedQRCodeAndWitData, isLoading: txLoading, refetch: refetchCombinedTransactions } = useQuery({
    queryKey: ["combined-transactions", chartFilters, merchantIdsForQuery],
    queryFn: async () => {
      const pageSize = 1000;
      const merchantsFilter = merchantIdsForQuery.length > 0 ? merchantIdsForQuery : ["__NO_MERCHANTS__"];

      let page = 1;
      let totalPages = 1;
      const allData = [];

      while (page <= totalPages && page <= 50) {
        const result = await combinedTransactionService.getCombinedQRCodeAndWitData(
          page,
          pageSize,
          "",
          chartFilters,
          merchantsFilter,
        );

        if (result?.data?.length) {
          allData.push(...result.data);
        }

        totalPages = result?.totalPages || 1;
        page += 1;
      }

      return {
        ...(allData.length > 0 ? { total: allData.length } : {}),
        data: allData,
      };
    },
    enabled: !!engineId && (allMerchants.length > 0 || allResellers.length > 0)
  });

  const transactions = React.useMemo(() => {
    const combinedData = combinedQRCodeAndWitData?.data || [];
    return combinedData.map((t) => ({
      ...t,
      transaction_id: t.transaction_id || t.payment_id || t.id,
      merchant_ref: t.merchant_ref || t.reference_id,
      type: normalizeTxType(t.type),
      status: (t.status || "").toLowerCase(),
      amount: parseAmount(t.amount),
      provider_display: t.provider_code || "-",
      currency: t.currency,
      client_bank_name: t.client_bank_name || t.bank_name || t.bank_code,
      bank_name: t.bank_name,
      bank_code: t.bank_code,
      merchant: t.merchant || {
        name: t.merchant_name,
        id: t.merchant_id,
        code: t.merchant_code,
      },
      reseller: t.reseller || {
        name: t.reseller_name,
        id: t.reseller_id,
        code: t.reseller_code,
      },
      created_date: t.created_date || t.createdAt,
      processed_at: t.processed_at || t.updatedAt,
      completed_at: t.processed_at || t.updatedAt,
      is_qr_code: normalizeTxType(t.type) === 'deposit',
    })).sort((a, b) => new Date(b.created_date || b.createdAt).getTime() - new Date(a.created_date || a.createdAt).getTime());
  }, [combinedQRCodeAndWitData]);

  const dateRange = React.useMemo(() => getDateRange(dateFilter, customDateRange), [dateFilter, customDateRange]);
  const filteredTx = React.useMemo(() => filterTransactionsByDateRange(transactions, dateRange.startDate, dateRange.endDate), [transactions, dateRange]);

  const totalVolume = dashboardMetrics.totalVolume || 0;
  const successDeposits = dashboardMetrics.totalTransactions || 0; // Using total transactions as success count for now if no specific count available
  // Or better, if we have filteredTx, we can use those for the chart and counts
  const successDepositsCount = filteredTx.filter(t => SUCCESS_STATUSES.has(t.status)).length;
  const failedDepositsCount = filteredTx.filter(t => t.status === "failed" || t.status === "expired" || t.status === "declined" || t.status === "cancelled").length;
  const totalWithdrawals = 0; // No engine withdrawals in the metrics provided
  const activeMerchants = merchants.filter(m => m.status === "active").length;
  const activeResellers = resellers.filter(r => r.status === "active").length;

  const chartData = React.useMemo(() => {
    const { startDate, endDate } = dateRange;
    const hoursDiff = differenceInHours(endDate, startDate);
    if (hoursDiff <= 48) {
      return eachHourOfInterval({ start: startDate, end: endDate }).map(hour => {
        const nextHour = new Date(hour.getTime() + 3600000);
        const txs = filteredTx.filter(t => {
          const d = new Date(t.created_date);
          return d >= hour && d < nextHour && SUCCESS_STATUSES.has(t.status);
        });
        return {
          date: format(hour, "HH:mm"),
          deposits: txs.filter(t => t.type === "deposit").reduce((s, t) => s + (t.amount || 0), 0),
          withdrawals: txs.filter(t => t.type === "withdrawal").reduce((s, t) => s + (t.amount || 0), 0),
        };
      });
    }
    return eachDayOfInterval({ start: startDate, end: endDate }).map(day => {
      const nextDay = new Date(day.getTime() + 86400000);
      const txs = filteredTx.filter(t => {
        const d = new Date(t.created_date);
        return d >= day && d < nextDay && SUCCESS_STATUSES.has(t.status);
      });
      return {
        date: format(day, "MMM d"),
        deposits: txs.filter(t => t.type === "deposit").reduce((s, t) => s + (t.amount || 0), 0),
        withdrawals: txs.filter(t => t.type === "withdrawal").reduce((s, t) => s + (t.amount || 0), 0),
      };
    });
  }, [filteredTx, dateRange]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        refetchDashboardMetrics(),
        refetchResellers(),
        refetchMerchants(),
        refetchCombinedTransactions(),
        refetchRecentTransactions(),
      ]);

      // Ensure any dependent cached views get fresh values as well.
      await queryClient.invalidateQueries({ queryKey: ["combined-transactions"] });
      await queryClient.invalidateQueries({ queryKey: ["recent-transactions"] });
    } finally {
      setTimeout(() => setIsRefreshing(false), 300);
    }
  };

  const CARDS = [
    { title: t("totalVolume"), value: `฿${Math.floor(dashboardMetrics.totalVolume || 0).toLocaleString()}`, icon: Wallet, gradient: "#22d3ee, #0284c7", accent: "#22d3ee", delay: 0 },
    { title: t("todayVolume"), value: `฿${Math.floor(dashboardMetrics.todayVolume || 0).toLocaleString()}`, icon: TrendingUp, gradient: "#34d399, #059669", accent: "#34d399", delay: 0.05 },
    { title: t("successRate"), value: `${(dashboardMetrics.successRate || 0).toFixed(2)}%`, icon: CheckCircle2, gradient: "#a78bfa, #7c3aed", accent: "#a78bfa", delay: 0.10 },
    { title: t("transactions"), value: (dashboardMetrics.totalTransactions || 0).toLocaleString(), icon: Activity, gradient: "#f87171, #dc2626", accent: "#f87171", delay: 0.15 },
    { title: t("merchants"), value: (dashboardMetrics.merchants || 0), icon: Building2, gradient: "#fb923c, #ea580c", accent: "#fb923c", delay: 0.20 },
    { title: t("resellers"), value: (dashboardMetrics.resellers || 0), icon: Users, gradient: "#818cf8, #4f46e5", accent: "#818cf8", delay: 0.25 },
  ];

  const QUICK = [
    { to: "EngineMerchants", icon: Building2, label: t("merchants"), gradient: "#fb923c, #ea580c", accent: "#fb923c", delay: 0.45 },
    { to: "EngineResellers", icon: Users, label: t("resellers"), gradient: "#a78bfa, #7c3aed", accent: "#a78bfa", delay: 0.48 },
    { to: "EngineTransactions", icon: Activity, label: t("transactions"), gradient: "#22d3ee, #0284c7", accent: "#22d3ee", delay: 0.51 },
    { to: "EngineRouting", icon: TrendingUp, label: t("routing"), gradient: "#34d399, #059669", accent: "#34d399", delay: 0.54 },
    { to: "EngineProviders", icon: Server, label: t("providers"), gradient: "#818cf8, #4f46e5", accent: "#818cf8", delay: 0.57 },
  ];

  return (
    <EngineLayout currentPage="EngineDashboard" resellerName={resellerName}>
      <div className="space-y-6 w-full max-w-full">

        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: -18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative rounded-2xl overflow-hidden p-5 sm:p-6"
          style={{
            background: "linear-gradient(135deg, rgba(15,23,42,0.8), rgba(8,14,30,0.9))",
            border: "1px solid rgba(34,211,238,0.12)",
          }}
        >
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(34,211,238,0.4), transparent)" }} />
          <div className="absolute top-0 right-0 w-48 h-48 pointer-events-none" style={{ background: "radial-gradient(circle at 100% 0%, rgba(34,211,238,0.07), transparent 70%)" }} />

          <div className="relative flex flex-wrap items-center justify-between gap-4">
            <div>
              {/* Top Live Row */}
              <div className="flex items-center gap-2 mb-1">

                {/* Animated Live Dot */}
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
                </span>

                {/* Live Text */}
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-cyan-400/80">
                  Live
                </span>

                {/* Date */}
                <span className="text-[11px] text-slate-500">
                  {format(new Date(), "EEE, MMM d")}
                </span>
              </div>

              {/* Dashboard Title */}
              <h1 className="
               text-[34px]
               font-[900]
               text-[#e2f4ff]
               tracking-[-0.04em]
               leading-[1.05]
               m-0
              ">
                {t("engine")}{" "}
                <span className="bg-gradient-to-r from-sky-400 via-cyan-400 to-cyan-200 bg-clip-text text-transparent">
                  {t("dashboard")}
                </span>
              </h1>

              {/* Welcome Text */}
              <p className="text-[13px] text-slate-500 font-medium mt-1">
                {t("welcomeBack")},{" "}
                <span className="text-cyan-400 font-bold">
                  {resellerName}
                </span>
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <DateFilter
                value={dateFilter}
                onChange={setDateFilter}
                customDateRange={customDateRange}
                onCustomDateRangeChange={setCustomDateRange}
              />
              <motion.button
                whileHover={{ scale: 1.06, rotate: isRefreshing ? 0 : 15 }}
                whileTap={{ scale: 0.93 }}
                onClick={handleRefresh}
                className="w-9 h-9 rounded-xl flex items-center justify-center cursor-pointer"
                style={{
                  background: "rgba(34,211,238,0.08)",
                  border: "1px solid rgba(34,211,238,0.18)",
                  outline: "none",
                  transition: "background 0.2s, box-shadow 0.2s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(34,211,238,0.14)"; e.currentTarget.style.boxShadow = "0 0 14px rgba(34,211,238,0.2)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(34,211,238,0.08)"; e.currentTarget.style.boxShadow = "none"; }}
              >
                <RefreshCw
                  className="h-4 w-4 text-cyan-400"
                  style={{ animation: isRefreshing ? "spin 0.8s linear infinite" : "none" }}
                />
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* ── Stat Cards ── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4">
          {CARDS.map((c, i) => (
            <PremiumStatCard key={i} {...c} />
          ))}
        </div>

        {/* ── Chart + Recent ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Chart */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="lg:col-span-2 relative rounded-2xl overflow-hidden p-5 sm:p-6"
            style={{
              background: "linear-gradient(135deg, rgba(15,23,42,0.85), rgba(8,14,30,0.9))",
              border: "1px solid rgba(34,211,238,0.09)",
            }}
          >
            <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(34,211,238,0.3), transparent)" }} />

            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <div>
                <h3 className="text-base font-bold text-slate-100">{t("volumeOverviewTitle")}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{t("transactionVolume")}</p>
              </div>
              <div className="flex items-center gap-4">
                {[{ color: "#22d3ee", label: t("deposits") }, { color: "#10b981", label: t("withdrawals") }].map(l => (
                  <div key={l.label} className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: l.color, boxShadow: `0 0 6px ${l.color}` }} />
                    <span className="text-xs text-slate-400">{l.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="h-[400px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
                  <defs>
                    <linearGradient id="engDepositGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="engWithdrawGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" vertical={false} />
                  <XAxis dataKey="date" stroke="#475569" fontSize={11} tick={{ fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <YAxis
                    stroke="#475569"
                    fontSize={11}
                    tick={{ fill: "#64748b" }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(value) => `฿${Number(value || 0).toLocaleString()}`}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <Area type="monotone" dataKey="deposits" name={t("deposits")} stroke="#06b6d4" strokeWidth={2.5} fill="url(#engDepositGrad)" dot={false} activeDot={{ r: 5, fill: "#06b6d4", stroke: "#0f172a", strokeWidth: 2 }} />
                  <Area type="monotone" dataKey="withdrawals" name={t("withdrawals")} stroke="#10b981" strokeWidth={2.5} fill="url(#engWithdrawGrad)" dot={false} activeDot={{ r: 5, fill: "#10b981", stroke: "#0f172a", strokeWidth: 2 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Quick Actions (Sidebar) */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.5 }}
            className="relative rounded-2xl overflow-hidden p-5"
            style={{
              background: "linear-gradient(135deg, rgba(15,23,42,0.85), rgba(8,14,30,0.9))",
              border: "1px solid rgba(34,211,238,0.09)",
            }}
          >
            <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(34,211,238,0.3), transparent)" }} />

            <div className="flex items-center gap-2 mb-6 px-1">
              <Zap className="h-4 w-4 text-cyan-400" />
              <h3 className="text-base font-bold text-slate-100">{t("quickActions")}</h3>
            </div>

            <div className="flex flex-col gap-3">
              {QUICK.map((q, i) => <QuickAction key={i} {...q} />)}
            </div>
          </motion.div>
        </div>

        {/* ── Recent Transactions (Full Width Bottom) ── */}
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="relative rounded-2xl overflow-hidden p-5 sm:p-6"
          style={{
            background: "linear-gradient(135deg, rgba(15,23,42,0.85), rgba(8,14,30,0.9))",
            border: "1px solid rgba(34,211,238,0.09)",
          }}
        >
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(34,211,238,0.3), transparent)" }} />

          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-cyan-400" />
              <h3 className="text-base font-bold text-slate-100">{t("recent")}</h3>
            </div>
            <Link to={createPageUrl("EngineTransactions")}>
              <motion.span
                whileHover={{ x: 3 }}
                className="text-xs font-semibold cursor-pointer flex items-center gap-1"
                style={{ color: "#22d3ee" }}
              >
                {t("viewAll")} <ArrowUpRight className="h-3 w-3" />
              </motion.span>
            </Link>
          </div>

          <div className="w-full">
            <AdminTransactionTable
              transactions={recentTx}
              isLoading={recentLoading || txLoading}
              onView={(tx) => setSelectedTx(tx)}
              showMerchant={true}
              showReseller={true}
              compact={false}
              merchants={allMerchants}
              resellers={allResellers}
            />
          </div>
        </motion.div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      <TransactionDetailPanel
        transaction={selectedTx}
        isOpen={!!selectedTx}
        onClose={() => setSelectedTx(null)}
      />
    </EngineLayout>
  );
}